import { HTMLCustomElement } from "@theme/html-custom-element";
import { Swiper } from "@theme/swiper";
import { breakpoints } from "@theme/helpers";

const FOOTER_ACCORDION_EASING = "ease-out";
const FOOTER_ACCORDION_DURATION_MS = 350;

/** Matches `base.css` `.from-large` (hidden below this width). */
const HIDDEN_USP_MQ = `(max-width: ${breakpoints.large - 1}px)`;

/**
 * USPs with `hide_on_mobile` use `.from-large` and are hidden below `breakpoints.large`.
 * Mirror their titles (and URLs when present) above the footer nav accordion on those viewports.
 *
 * @param {HTMLElement} host `site-footer`
 * @param {HTMLElement} root
 * @param {MediaQueryList} mq
 */
function syncFooterHiddenUspMirror(host, root, mq) {
  if (!root) return;

  if (!mq.matches) {
    root.hidden = true;
    root.replaceChildren();
    return;
  }

  const hidden = host.querySelectorAll(".footer-usp.from-large");
  if (!hidden.length) {
    root.hidden = true;
    root.replaceChildren();
    return;
  }

  root.hidden = false;
  root.replaceChildren();

  for (const usp of hidden) {
    const titleSpan = usp.querySelector(".footer-usp__title span");
    if (!titleSpan) continue;

    const label = titleSpan.textContent.trim();
    if (!label) continue;

    const inner = usp.querySelector(".footer-usp__inner");
    const isLink = inner?.tagName === "A" && inner.getAttribute("href");

    if (isLink) {
      const a = document.createElement("a");
      a.href = inner.href;
      a.className =
        "footer-nav__mobile-usp footer-nav__summary button-reset text-heading-serif-sm";
      a.textContent = label;
      const target = inner.getAttribute("target");
      const rel = inner.getAttribute("rel");
      if (target) a.target = target;
      if (rel) a.rel = rel;
      root.appendChild(a);
    } else {
      const p = document.createElement("p");
      p.className =
        "footer-nav__mobile-usp footer-nav__mobile-usp--text footer-nav__summary text-heading-serif-sm";
      p.textContent = label;
      root.appendChild(p);
    }
  }
}

/**
 * @param {HTMLElement} host
 * @param {HTMLDetailsElement} navDisclosure
 * @param {MediaQueryList} mq
 * @returns {HTMLElement | null}
 */
function ensureFooterHiddenUspMirrorRoot(host, navDisclosure, mq) {
  if (!navDisclosure?.parentElement) return null;

  const parent = navDisclosure.parentElement;
  const existing = parent.querySelector(
    ":scope > .footer-nav__mobile-hidden-usps",
  );
  if (existing) {
    syncFooterHiddenUspMirror(host, existing, mq);
    return existing;
  }

  const root = document.createElement("div");
  root.className = "footer-nav__mobile-hidden-usps";
  root.hidden = true;
  parent.insertBefore(root, navDisclosure);
  syncFooterHiddenUspMirror(host, root, mq);
  return root;
}

/**
 * Mobile `<details>` accordion with max-height animation; desktop forces open and clears motion styles.
 * @param {HTMLElement} host `site-footer` (uses `on` and `events` from HTMLCustomElement).
 * @param {HTMLDetailsElement} disclosure
 * @param {HTMLElement} panel
 * @param {MediaQueryList} mq
 * @param {MediaQueryList} reduceMotion
 */
function bindFooterAccordion(host, disclosure, panel, mq, reduceMotion) {
  if (!disclosure || !panel) return;

  const summary = disclosure.querySelector("summary");

  const clearPanelAnimation = () => {
    panel.style.transition = "";
    panel.style.maxHeight = "";
    panel.style.overflow = "";
  };

  const setPanelMotion = () => {
    panel.style.transition = `max-height ${FOOTER_ACCORDION_DURATION_MS}ms ${FOOTER_ACCORDION_EASING}`;
    panel.style.overflow = "hidden";
  };

  const setPanelHeight = (px) => {
    setPanelMotion();
    panel.style.maxHeight = `${px}px`;
  };

  const syncMobilePanelToState = () => {
    if (reduceMotion.matches) {
      panel.style.transition = "none";
      if (disclosure.open) {
        panel.style.overflow = "visible";
        panel.style.maxHeight = "none";
      } else {
        panel.style.overflow = "hidden";
        panel.style.maxHeight = "0";
      }
      return;
    }
    if (disclosure.open) {
      requestAnimationFrame(() => {
        setPanelHeight(panel.scrollHeight);
      });
    } else {
      setPanelHeight(0);
    }
  };

  const syncSection = () => {
    disclosure.open = mq.matches;
    if (mq.matches) {
      clearPanelAnimation();
    } else {
      syncMobilePanelToState();
    }
  };

  syncSection();
  host.on("change", mq, syncSection);

  host.on("toggle", disclosure, () => {
    if (mq.matches) return;
    if (reduceMotion.matches) {
      panel.style.transition = "none";
      if (disclosure.open) {
        panel.style.overflow = "visible";
        panel.style.maxHeight = "none";
      } else {
        panel.style.overflow = "hidden";
        panel.style.maxHeight = "0";
      }
      return;
    }
    if (disclosure.open) {
      requestAnimationFrame(() => {
        setPanelHeight(panel.scrollHeight);
      });
    } else {
      setPanelHeight(0);
    }
  });

  let closePending = false;

  if (summary) {
    host.on(
      "click",
      summary,
      (event) => {
        if (mq.matches || reduceMotion.matches) return;
        if (!disclosure.open || closePending) return;
        event.preventDefault();
        closePending = true;

        let closeFinished = false;
        let closeSafetyTimerId = 0;

        const finishClose = () => {
          if (closeFinished) return;
          closeFinished = true;
          panel.removeEventListener("transitionend", onTransitionEnd);
          if (closeSafetyTimerId) {
            window.clearTimeout(closeSafetyTimerId);
          }
          closePending = false;
          disclosure.removeAttribute("open");
        };

        const onTransitionEnd = (transitionEvent) => {
          if (
            transitionEvent.target !== panel ||
            transitionEvent.propertyName !== "max-height"
          ) {
            return;
          }
          finishClose();
        };

        setPanelHeight(panel.scrollHeight);
        requestAnimationFrame(() => {
          setPanelHeight(0);
        });
        panel.addEventListener("transitionend", onTransitionEnd);
        closeSafetyTimerId = window.setTimeout(
          finishClose,
          FOOTER_ACCORDION_DURATION_MS + 80,
        );
      },
      { capture: true },
    );
  }

  const resizeObserver = new ResizeObserver(() => {
    if (!mq.matches && disclosure.open && !reduceMotion.matches) {
      requestAnimationFrame(() => {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      });
    }
  });
  resizeObserver.observe(panel);
  host.events.push(() => resizeObserver.disconnect());
}

class SiteFooter extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    const mq = window.matchMedia(`(min-width: ${breakpoints.medium}px)`);
    const mqHiddenUsps = window.matchMedia(HIDDEN_USP_MQ);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    bindFooterAccordion(
      this,
      this.refs.socialDisclosure,
      this.refs.socialPanel,
      mq,
      reduceMotion,
    );
    bindFooterAccordion(
      this,
      this.refs.navDisclosure,
      this.refs.navPanel,
      mq,
      reduceMotion,
    );

    let mirrorRoot = null;
    if (this.refs.navDisclosure) {
      mirrorRoot = ensureFooterHiddenUspMirrorRoot(
        this,
        this.refs.navDisclosure,
        mqHiddenUsps,
      );
      this.on("change", mqHiddenUsps, () => {
        syncFooterHiddenUspMirror(this, mirrorRoot, mqHiddenUsps);
      });
    }

    const scheduleMirrorSync = () => {
      if (!mirrorRoot) return;
      syncFooterHiddenUspMirror(this, mirrorRoot, mqHiddenUsps);
    };

    const mirrorObserver = new MutationObserver((records) => {
      if (!mirrorRoot) return;
      const onlyMirrorSubtree = records.every(
        (r) => r.target === mirrorRoot || mirrorRoot.contains(r.target),
      );
      if (onlyMirrorSubtree) return;
      scheduleMirrorSync();
    });
    mirrorObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    this.events.push(() => mirrorObserver.disconnect());

    if (this.refs.usps) {
      new Swiper(this.refs.usps, {
        effect: "fade",
        fadeEffect: {
          crossFade: false,
        },
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        scrollbar: {
          el: this.refs.scrollbar,
        },
        slidesPerView: 1,
        breakpoints: {
          [breakpoints.medium]: {
            slidesPerView: 3,
            effect: "none",
          },
        },
      });
    }
  }
}

if (!customElements.get("site-footer"))
  customElements.define("site-footer", SiteFooter);
